import { Component, useContext } from "react";
import RadarGraph from "./RadarGraph";
import BarGraph from "./BarGraph";
import "./PageCharts.css";
import "./EditPageCharts.css";
import { FormattedMessage } from "react-intl";
import messages from "../lang/preferences";
import HelpModal from "./HelpModal";
import { getTopExtremeTraitsUpperThird } from "../utils/traitsOrderMedian";
import ScopeReportIntroPage from "./ScopeReportIntroPage";
import getThirdPosition from "../utils/getThirdPosition";
import ppeFullLogo from '../ressources/logo-with-text-green.svg'; 
import CopingDefinitionsPages from "./CopingDefinitionsPages";
import CopingDefinitionsPages2 from "./CopingDefinitionsPages2";
import { LanguageContext } from '../contexts/LanguageContext';

// Dynamically set PAGE_NUMBERS based on locale (German needs extra page)
function getPageNumbers(locale, hasFaitsOpinions = false) {
  console.log("Determining page numbers for locale:", locale, "hasFaitsOpinions:", hasFaitsOpinions);
  if (hasFaitsOpinions) {
    // If German or French, insert an extra page for CopingDefinitionsPages
    if (locale && typeof locale === 'string' && (locale.toLowerCase().startsWith('de') || locale.toLowerCase().startsWith('fr'))) {
      return [3, 8, 12, 14, 15, 19];
    }
    return [3, 8, 11, 13, 14, 18];
  }
  if (locale && typeof locale === 'string' && (locale.toLowerCase().startsWith('de') || locale.toLowerCase().startsWith('fr'))) {
    return [3, 7, 8, 11, 12, 16];
  }
  return [3, 7, 10, 12, 13, 17];
}

const FO_KEYS = Array.from({ length: 18 }, (_, i) => `FAITSOPINIONSQUESTION${i + 1}`);

function getFaitsOpinionsTexts() {
  return FO_KEYS.map((key) => {
    const message = messages[key];
    return message && message.defaultMessage ? String(message.defaultMessage).toLowerCase() : null;
  }).filter(Boolean);
}

function isFaitsGraphByContent(graph, foTexts, debugIndex = null) {
  const data = graph && graph.data ? graph.data : graph;
  if (!Array.isArray(data)) return false;

  let matches = 0;
  for (const item of data) {
    const text = String(item?.fr || item?.en || item?.label || "").toLowerCase();
    for (const foText of foTexts) {
      if (!foText) continue;
      if (text.includes(foText) || foText.includes(text) || text.includes(foText.slice(0, 20))) {
        matches++;
        break;
      }
    }
  }

  if (debugIndex !== null && matches > 0) {
    console.log(`Graph[${debugIndex}] FO match count:`, matches, 'graph:', graph);
  }

  return matches >= 2;
}

function getFaitsOpinionsMeta(graphs, { debugMatches = false } = {}) {
  const safeGraphs = Array.isArray(graphs) ? graphs : [];
  const foTexts = getFaitsOpinionsTexts();
  const foGraph = safeGraphs.find((graph) => (
    graph && (
      graph.type === 'faits_opinions' ||
      String(graph.title).toLowerCase().includes('faits_opinions') ||
      String(graph.title).toLowerCase().includes('faits_opinions_score')
    )
  ));

  const detectedGraph = safeGraphs.find((graph, index) => (
    isFaitsGraphByContent(graph, foTexts, debugMatches ? index : null)
  ));

  const hasFaitsOpinions = Boolean(foGraph || detectedGraph);
  const faitsOpinionsGraph = foGraph || detectedGraph || null;
  const faitsOpinionsData = foGraph || (detectedGraph && detectedGraph.data) || null;
  const foDisplayData = Array.isArray(faitsOpinionsData)
    ? faitsOpinionsData
    : (faitsOpinionsData && Array.isArray(faitsOpinionsData.data) ? faitsOpinionsData.data : null);

  return {
    foGraph,
    faitsOpinionsGraph,
    hasFaitsOpinions,
    faitsOpinionsData,
    foDisplayData,
  };
}

function getFirstSectionPageNumbers(basePage, hasFaitsOpinions) {
  return {
    traitsPage1: basePage + 1,
    traitsPage2: basePage + 2,
    cognitivePage: basePage + 3,
    faitsOpinionsPage: hasFaitsOpinions ? basePage + 4 : null,
  };
}

const formatDateForDisplay = (value, locale, options) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString(locale, options);
};

function getTraitPerentile(trait, pageNumber = 0) {
  let traitKey = trait.en === "Self assurance" ? "Selfassurance" : trait.en;
  traitKey = traitKey.toUpperCase().replace(/ /g, "_");

  const label = trait.extremeLabel === "lowerThird" ? "LOW" : "HIGH";
  const percentile = trait.extremeLabel === "lowerThird" ? "25" : "100";

  if(pageNumber === 1 && trait.extremeLabel === "lowerThird") {
    trait.extremeLabel = "upperThird";
    //console.log("Special case triggered",);
  }

  const prefix = (pageNumber === 6 || pageNumber === 5)
    ? `SCOPE_REPORT_${traitKey}`
    : `SCOPE_REPORT_${traitKey}_AXIS_${label}`;
  const percentilePrefix = `SCOPE_REPORT_${traitKey}_PERCENTILE_${percentile}`;
  const percentileTitle = `SCOPE_REPORT_PERCENTILE_${percentile}_TITLE`;

  // Only include optimize/motivation if not page 6
  if (pageNumber === 6 || pageNumber === 5) {
    return {
      title: `${prefix}`,
      description: `${prefix}_DESC`,
      percentile: `${percentileTitle}`,
      strengthsIntro: `${percentilePrefix}_STRENGTHS_INTRO`,
      strengths1: `${percentilePrefix}_STRENGTHS_1`,
      strengths2: `${percentilePrefix}_STRENGTHS_2`,
      strengths3: `${percentilePrefix}_STRENGTHS_3`,
      developmentIntro: `${percentilePrefix}_DEVELOPMENT_INTRO`,
      development1: `${percentilePrefix}_DEVELOPMENT_1`,
      development2: `${percentilePrefix}_DEVELOPMENT_2`,
      development3: `${percentilePrefix}_DEVELOPMENT_3`,
      teamAdvice: `${percentilePrefix}_TEAM_ADVICE`,
      collaboration: `${percentilePrefix}_COLLABORATION`,
      management: `${percentilePrefix}_MANAGEMENT`,
      recognition: `${percentilePrefix}_RECOGNITION`,
      teamKnow: `${percentilePrefix}_TEAM_KNOW`,
      teamKnowLabel: `SCOPE_REPORT_TEAM_KNOW_LABEL`,
      teamAdviceLabel: `SCOPE_REPORT_TEAM_ADVICE_LABEL`,
    };
  } else {
    return {
      title: `${prefix}`,
      description: `${prefix}_DESC`,
      percentile: `${percentilePrefix}_TITLE`,
      strengthsIntro: `${percentilePrefix}_STRENGTHS_INTRO`,
      strengths1: `${percentilePrefix}_STRENGTHS_1`,
      strengths2: `${percentilePrefix}_STRENGTHS_2`,
      strengths3: `${percentilePrefix}_STRENGTHS_3`,
      developmentIntro: `${percentilePrefix}_DEVELOPMENT_INTRO`,
      development1: `${percentilePrefix}_DEVELOPMENT_1`,
      development2: `${percentilePrefix}_DEVELOPMENT_2`,
      development3: `${percentilePrefix}_DEVELOPMENT_3`,
      optimize4me: `SCOPE_REPORT_OPTIMIZE_FOR_ME_LABEL`,
      leversMotivation: `SCOPE_REPORT_LEVERS_OF_MOTIVATION_LABEL`,
      optimize: `${percentilePrefix}_OPTIMIZE`,
      motivation: `${percentilePrefix}_MOTIVATION`,
      teamAdvice: `${percentilePrefix}_TEAM_ADVICE`,
      teamAdviceLabel: `SCOPE_REPORT_TEAM_ADVICE_LABEL`,
    };
  }
}

// Helper to safely render FormattedMessage
function SafeFormattedMessage({ msgKey }) {
  const msg = messages[msgKey];
  const valid = msg && typeof msg.id === "string" && msg.id.length > 0;
  if (valid) {
    return <FormattedMessage {...msg} />;
  }
  return msgKey || "";
}

const getDurationLocaleConfig = (locale) => {
  const normalized = String(locale || 'en').toLowerCase();

  if (normalized.startsWith('ja')) {
    return { h: '時間', m: '分', s: '秒', compact: true };
  }

  if (normalized.startsWith('zh-tw') || normalized.startsWith('zh-hk') || normalized.includes('hant')) {
    return { h: '小時', m: '分', s: '秒', compact: true };
  }

  if (normalized.startsWith('zh')) {
    return { h: '小时', m: '分', s: '秒', compact: true };
  }

  return { h: 'h', m: 'm', s: 's', compact: false };
};

const formatDuration = (duration, locale) => {
  const unitCfg = getDurationLocaleConfig(locale);

  const formatParts = (hours, minutes, seconds) => {
    if (unitCfg.compact) {
      return `${hours}${unitCfg.h}${minutes}${unitCfg.m}${seconds}${unitCfg.s}`;
    }
    return `${hours}${unitCfg.h} ${minutes}${unitCfg.m} ${seconds}${unitCfg.s}`;
  };

  const fallback = formatParts(0, 33, 33);

  const buildDuration = (hours, minutes, seconds) => {
    const h = Number(hours);
    const m = Number(minutes);
    const s = Number(seconds);

    if (!Number.isFinite(h) || !Number.isFinite(m) || !Number.isFinite(s)) {
      return fallback;
    }

    if (h < 0 || m < 0 || s < 0) {
      return fallback;
    }

    return formatParts(Math.floor(h), Math.floor(m), Math.floor(s));
  };

  try {
    if (duration === null || typeof duration === 'undefined' || duration === '' || duration === 'null') {
      return fallback;
    }

    if (typeof duration === 'object' && duration !== null && 'hours' in duration) {
      return buildDuration(duration.hours, duration.minutes, duration.seconds);
    }
    
    if (typeof duration === 'string') {
        const compactMatch = duration.match(/^(\d+)h\s*(\d+)m\s*(\d+)s$/i);
        if (compactMatch) {
          return buildDuration(compactMatch[1], compactMatch[2], compactMatch[3]);
        }

        const genericThreeNumbers = duration.match(/(\d+)\D+(\d+)\D+(\d+)/);
        if (genericThreeNumbers) {
          return buildDuration(genericThreeNumbers[1], genericThreeNumbers[2], genericThreeNumbers[3]);
        }

        const jsonString = duration
          .replace(/(\w+):/g, '"$1":')  // Add quotes around property names
          .replace(/'/g, '"');          // Replace single quotes with double quotes
        
        try {
          const parsed = JSON.parse(jsonString);
          return buildDuration(parsed.hours, parsed.minutes, parsed.seconds);
        } catch (e2) {
          //console.log("Failed to convert to JSON");
        }
    }
    
    return fallback;
  } catch (e) {
    //console.error("ERROR in formatDuration:", e);
    return fallback;
  }
};

function renderTraitDetailsList({
  data,
  type,
  filterIdx = 2,
  getTopTraits = getTopExtremeTraitsUpperThird,
  liClassName = "list-counter-marker",
  pageNumber = 1 
})
{
      const topTraits = getTopTraits(data, type);
      // If any selected trait was filled from the middle third, decide whether it should
      // map to LOWER or UPPER texts based on which half of the middle third it sits in.
      // If it's in the lower half of the middleThird, pivot to lowerThird texts.
      try {
        const getUserScore = trait => typeof trait.user === 'number' ? trait.user : parseFloat(trait.user);
        topTraits.forEach(trait => {
          if (!trait || trait.extremeLabel !== 'middleThird') return;
          const userScore = getUserScore(trait);
          let getMiddleUpper, getMiddleBottom;
          if (type === 'bar') {
            const mt = typeof trait.middleThird === 'number' ? trait.middleThird : parseFloat(trait.middleThird);
            const lt = typeof trait.lowerThird === 'number' ? trait.lowerThird : parseFloat(trait.lowerThird);
            getMiddleUpper = mt + lt;
            getMiddleBottom = lt;
          } else {
            getMiddleUpper = typeof trait.middleThird === 'number' ? trait.middleThird : parseFloat(trait.middleThird);
            getMiddleBottom = typeof trait.lowerThird === 'number' ? trait.lowerThird : parseFloat(trait.lowerThird);
          }
          const middleCenter = (getMiddleUpper + getMiddleBottom) / 2;
          if (!isNaN(userScore) && userScore <= middleCenter) {
            trait.extremeLabel = 'lowerThird';
          } else {
            trait.extremeLabel = 'upperThird';
          }
        });
      } catch (e) {
        // defensive: if anything goes wrong, leave labels as-is
        console.warn('Failed to canonicalize middleThird labels', e);
      }
      return topTraits.map((trait, idx) => {
        if (typeof filterIdx === "function" ? !filterIdx(idx, trait) : idx !== filterIdx) return null;
        const traitDetails = getTraitPerentile(trait, pageNumber);
        const extremeKey = trait.extremeLabel ? trait.extremeLabel.toUpperCase() : "";
        let getDetails = traitDetails.description;
        let managerDetails = false;
        //console.log("traitDetails", traitDetails, "extremeKey", extremeKey, "pageNumber", pageNumber);
        if(pageNumber === 6){
          if(extremeKey === "LOWERTHIRD") {
            
            getDetails = `SCOPE_REPORT_${trait.en.toUpperCase().replace(/ /g, "_")}_DESC_LOW`;
            //console.log("traitDetails", getDetails, trait.en.toUpperCase().replace(/ /g, "_"));
            managerDetails = `SCOPE_REPORT_${trait.en.toUpperCase().replace(/ /g, "_")}_MANAGER_DESC_LOW`;
          }
          else{
            getDetails = `SCOPE_REPORT_${trait.en.toUpperCase().replace(/ /g, "_")}_DESC`;
            managerDetails = `SCOPE_REPORT_${trait.en.toUpperCase().replace(/ /g, "_")}_MANAGER_DESC`;
          }
          
        }
            if(pageNumber === 5){
          if(extremeKey === "LOWERTHIRD") {
            
            getDetails = `SCOPE_REPORT_${trait.en.toUpperCase().replace(/ /g, "_")}_DESC_LOW`;
            //console.log("traitDetails", getDetails, trait.en.toUpperCase().replace(/ /g, "_"));
            managerDetails = `SCOPE_REPORT_${trait.en.toUpperCase().replace(/ /g, "_")}_MANAGER_DESC_LOW`;
          }
          else{
            getDetails = `SCOPE_REPORT_${trait.en.toUpperCase().replace(/ /g, "_")}_DESC`;
            managerDetails = `SCOPE_REPORT_${trait.en.toUpperCase().replace(/ /g, "_")}_MANAGER_DESC`;
          }
          
        }
        if(pageNumber === 1 ) { //console.log("PAGE 1 traitDetails",traitDetails, "extremeKey", extremeKey);
          }
        return (
                  <li key={idx + "_traits"} className={liClassName}>
                    <b className="font-18">
                      <SafeFormattedMessage msgKey={traitDetails.title} />
                    </b>  - {(
                      <SafeFormattedMessage msgKey={extremeKey} />
                    )}
                    {/*typeof trait.user === "number" ? trait.user : parseFloat(trait.user)*/}
                    <span className="trait-details-box">
                      <br />
                      <SafeFormattedMessage msgKey={getDetails} />
                      <br />
                      { (pageNumber === 5 || pageNumber === 6) && managerDetails && <div className="trait-details-list-box"><b><SafeFormattedMessage msgKey={'SCOPE_REPORT_MANAGER_SUMMARY_LABEL'} /></b> <SafeFormattedMessage msgKey={managerDetails} /></div>}
                      {/* HIDE percentile <span className="percentile-subtitle"><i><SafeFormattedMessage msgKey={traitDetails.percentile} /></i></span>*/}
                      <span className="trait-details-list-box">
                        {/* Strengths List */}
                        <span>
                          <span>
                            <SafeFormattedMessage msgKey={traitDetails.strengthsIntro} />
                          </span>
                          <ul className="trait-strengths-list" style={{ margin: 0, paddingLeft: 24 }}>
                            {[traitDetails.strengths1, traitDetails.strengths2, traitDetails.strengths3]
                              .map((key, idx) => {
                                const msg = messages[key];
                                if (!msg || !msg.defaultMessage) return null;
                                return (
                                  <li className="trait-strengths-list-item" key={idx}>
                                    <SafeFormattedMessage msgKey={key} />
                                  </li>
                                );
                              })}
                          </ul>
                        </span>
                      </span>
                      <span className="trait-details-list-box">
                        <span>
                          <span>
                            <SafeFormattedMessage msgKey={traitDetails.developmentIntro} />
                          </span>
                          <ul className="trait-development-list" style={{ margin: 0, paddingLeft: 24 }}>
                            {[traitDetails.development1, traitDetails.development2, traitDetails.development3]
                              .map((key, idx) => {
                                const msg = messages[key];
                                if (!msg || !msg.defaultMessage) return null;
                                return (
                                  <li className="trait-development-list-item" key={idx}>
                                    <SafeFormattedMessage msgKey={key} />
                                  </li>
                                );
                              })}
                          </ul>
                        </span>
                      </span>
                      {/* Optimize */}
                      {pageNumber !== 6 && (
                        <span className="trait-details-list-box">
                          <span>
                            <b><SafeFormattedMessage
                              msgKey={traitDetails.optimize4me}
                            /></b>
                            <SafeFormattedMessage
                              msgKey={traitDetails.optimize}
                            />
                          </span>
                        </span>
                      )}
                      {/* Motivation */}
                      {pageNumber !== 6 && (
                        <span className="trait-details-list-box">
                          <span>
                            <b><SafeFormattedMessage
                              msgKey={traitDetails.leversMotivation}
                            /></b>
                            <SafeFormattedMessage
                              msgKey={traitDetails.motivation}
                            />
                          </span>
                        </span>
                      )}

                      {/* Team Advice personality */}
                      {pageNumber === 1 && (
                        <span className="trait-details-list-box">
                        <span className="trait-details-list-box">
                          <span>
                            <b><SafeFormattedMessage
                              msgKey={traitDetails.teamAdviceLabel}
                            /></b>
                            <SafeFormattedMessage
                              msgKey={traitDetails.teamAdvice}
                            />
                          </span>
                        </span>
                        </span>
                      )}
                      {/* For pageNumber 5, show the extra fields */}
                      {(pageNumber === 5) && (
                        <>
                          {/* For Collaboration */}
                          <span className="trait-details-list-box">
                            <span>
                              <b><SafeFormattedMessage
                                msgKey={"SCOPE_REPORT_COLLABORATION_LABEL"}
                              /></b>
                              <SafeFormattedMessage
                                msgKey={traitDetails.collaboration}
                              />
                            </span>
                          </span>
                          <span className="trait-details-list-box">
                            <span>
                              <b><SafeFormattedMessage
                                msgKey={"SCOPE_REPORT_MANAGEMENT_LABEL"}
                              /></b>
                              <SafeFormattedMessage
                                msgKey={traitDetails.management}
                              />
                            </span>
                          </span>
                          <span className="trait-details-list-box">
                            <span>
                              <b><SafeFormattedMessage
                                msgKey={"SCOPE_REPORT_RECOGNITION_LABEL"}
                              /></b>
                              <SafeFormattedMessage
                                msgKey={traitDetails.recognition}
                              />
                            </span>
                          </span>
                        {/* Team Advice */}
                        <span className="trait-details-list-box">
                          <span>
                            <b><SafeFormattedMessage
                              msgKey={traitDetails.teamAdviceLabel}
                            /></b>
                            <SafeFormattedMessage
                              msgKey={traitDetails.teamAdvice}
                            />
                          </span>
                        </span>
                          <span className="trait-details-list-box">
                          <span>
                            <b><SafeFormattedMessage
                              msgKey={traitDetails.teamKnowLabel}
                            /></b>
                            <SafeFormattedMessage
                              msgKey={traitDetails.teamKnow}
                            />
                          </span>
                        </span>
                        </>
                      )}

                      {/* For page number 6, show the extra fields */}
                      {(pageNumber === 6) && (
                        <>
                        {/* Team Advice */}
                        <span className="trait-details-list-box">
                          <span>
                            <b><SafeFormattedMessage
                              msgKey={traitDetails.teamAdviceLabel}
                            /></b>
                            <SafeFormattedMessage
                              msgKey={traitDetails.teamAdvice}
                            />
                          </span>
                        </span>
                          <span className="trait-details-list-box">
                            <span>
                              <b><SafeFormattedMessage
                                msgKey={"SCOPE_REPORT_COLLABORATION_LABEL"}
                              /></b>
                              <SafeFormattedMessage
                                msgKey={traitDetails.collaboration}
                              />
                            </span>
                          </span>
                          <span className="trait-details-list-box">
                            <span>
                              <b><SafeFormattedMessage
                                msgKey={"SCOPE_REPORT_MANAGEMENT_LABEL"}
                              /></b>
                              <SafeFormattedMessage
                                msgKey={traitDetails.management}
                              />
                            </span>
                          </span>
                          <span className="trait-details-list-box">
                            <span>
                              <b><SafeFormattedMessage
                                msgKey={"SCOPE_REPORT_RECOGNITION_LABEL"}
                              /></b>
                              <SafeFormattedMessage
                                msgKey={traitDetails.recognition}
                              />
                            </span>
                          </span>
                          <span className="trait-details-list-box">
                            <span>
                              <b><SafeFormattedMessage
                                msgKey={"SCOPE_REPORT_ESSENTIAL_FOR_ME_LABEL"}
                              /></b>
                              <SafeFormattedMessage
                                msgKey={traitDetails.teamKnow}
                              />
                            </span>
                          </span>
                        </>
                      )}
                    </span>
                  </li>
        );
      });
  }

function PageChartsWithContext(props) {
  // Always use the context locale (matches WrapperIntl)
  const { locale } = useContext(LanguageContext);
  //console.log("LOCALE:::::", locale);
  return <PageCharts {...props} locale={locale} localeDisplay={locale} />;
}

export class PageCharts extends Component {
  state = {
    premium: true,
    loading: false,
    widthParentBar: "90%",
    heightParentBar: "50%",
    widthParentRadar: "90%",
    heightParentRadar: "50%",
    many: "top",
    graphs: [],
  };

  componentDidUpdate(prevProps, prevState) {
    // Only log when graphs change (after data loads)
    if (prevState.graphs !== this.state.graphs) {
      const { hasFaitsOpinions } = getFaitsOpinionsMeta(this.state.graphs);
      const pageNumbers = getPageNumbers(this.props.locale, hasFaitsOpinions);
      console.log('[componentDidUpdate] PageCharts: this.state.graphs', this.state.graphs);
      console.log('[componentDidUpdate] PageCharts: hasFaitsOpinions', hasFaitsOpinions);
      console.log('[componentDidUpdate] PageCharts: pageNumbers', pageNumbers);
    }
  }

  renderPoste = () => {
    try {
      const p = this.getPoste();
      if (!p) return "";
      // If getPoste returned an i18n key like INFOPERSO5A..E, render it localized
      if (/^INFOPERSO5[A-E]$/.test(p)) {
        return <SafeFormattedMessage msgKey={p} />;
      }
      // Otherwise return the raw value
      return p;
    } catch (e) {
      return "";
    }
  };

  _displayGraph = (type, data, cp = false) => {
    switch (type) {
      case "radar":
        return (
          <div className="PageCharts-parentRadar">
            {data && data.length === 3 && 
              <div>
                <div className="action-orientation-box">
                  <div className="action-orientation-box expressivity-box">
                    <b><SafeFormattedMessage msgKey="SCOPE_REPORT_EXPRESSIVENESS" /></b>
                    <div className="expressivity-desc emotionality-desc">
                      <SafeFormattedMessage msgKey="SCOPE_REPORT_EXPRESSIVENESS_DESC" />
                    </div>
                  </div>
                </div>
                <div className="action-orientation-box">
                  <div className="action-orientation-box regulation-box">
                    <b><SafeFormattedMessage msgKey="SCOPE_REPORT_AFFECTIVEREGULATION" /></b>
                    <div className="emotionality-desc regulation-desc">
                      <SafeFormattedMessage msgKey="SCOPE_REPORT_AFFECTIVEREGULATION_DESC" />
                    </div>
                  </div>
                </div>
                <div className="action-orientation-box">
                  <div className="action-orientation-box resilience-box">
                    <b><SafeFormattedMessage msgKey="SCOPE_REPORT_EMOTIONALRESILIENCE" /></b>
                    <div className="emotionality-desc resilience-desc">
                      <SafeFormattedMessage msgKey="SCOPE_REPORT_EMOTIONALRESILIENCE_DESC" />
                    </div>
                  </div>
                </div>
              </div>
            }
           <RadarGraph data={data} />
          </div>
        );
      case "bar":
        return (     
          <div className={`PageCharts-parentBar${cp ? '-horizontal' : ''}`}>
            { /*console.log("DATA::::", data[8])*/}
            { !cp && Array.isArray(data) && data.some(item => item?.fr === "Style cognitif") && 
              <div className="scope-personality-table-container">
                  <table className="scope-table-fullwidth scope-personality-table-spacing">
                    <tbody>
                      {/* Extraversion */}
                      <tr>
                        <td className="scope-table-col">
                          <div className="scope-table-title-left">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_EXTRAVERSION_AXIS_LOW" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_EXTRAVERSION_AXIS_LOW_DESC" />
                        </td>
                        <td className="col-middle">
                        </td>
                        <td className="scope-table-col  col-right">
                          <div className="scope-table-title-right">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_EXTRAVERSION_AXIS_HIGH" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_EXTRAVERSION_AXIS_HIGH_DESC" />
                        </td>
                      </tr>
                      {/* Excitement Seek */}
                      <tr>
                        <td className="scope-table-col">
                          <div className="scope-table-title-left">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_EXCITEMENT_SEEKING_AXIS_LOW" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_EXCITEMENT_SEEKING_AXIS_LOW_DESC" />
                        </td>
                        <td className="col-middle">
                        </td>
                        <td className="scope-table-col  col-right">
                          <div className="scope-table-title-right">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_EXCITEMENT_SEEKING_AXIS_HIGH" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_EXCITEMENT_SEEKING_AXIS_HIGH_DESC" />
                        </td>
                      </tr>
                      {/* Impulsivity */}
                      <tr>
                        <td className="scope-table-col">
                          <div className="scope-table-title-left">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_IMPULSIVITY_AXIS_LOW" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_IMPULSIVITY_AXIS_LOW_DESC" />
                        </td>
                        <td className="col-middle">
                        </td>
                        <td className="scope-table-col  col-right">
                          <div className="scope-table-title-right">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_IMPULSIVITY_AXIS_HIGH" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_IMPULSIVITY_AXIS_HIGH_DESC" />
                        </td>
                      </tr>
                      {/* Pessimism */}
                      <tr>
                        <td className="scope-table-col">
                          <div className="scope-table-title-left">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_PESSIMISM_AXIS_LOW" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_PESSIMISM_AXIS_LOW_DESC" />
                        </td>
                        <td className="col-middle">
                        </td>
                        <td className="scope-table-col  col-right">
                          <div className="scope-table-title-right">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_PESSIMISM_AXIS_HIGH" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_PESSIMISM_AXIS_HIGH_DESC" />
                        </td>
                      </tr>
                      {/* Self-Assurance */}
                      <tr>
                        <td className="scope-table-col">
                          <div className="scope-table-title-left">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_SELFASSURANCE_AXIS_LOW" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_SELFASSURANCE_AXIS_LOW_DESC" />
                        </td>
                        <td className="col-middle">
                        </td>
                        <td className="scope-table-col  col-right">
                          <div className="scope-table-title-right">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_SELFASSURANCE_AXIS_HIGH" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_SELFASSURANCE_AXIS_HIGH_DESC" />
                        </td>
                      </tr>
                      {/* Dominance */}
                      <tr>
                        <td className="scope-table-col">
                          <div className="scope-table-title-left">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_DOMINANCE_AXIS_LOW" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_DOMINANCE_AXIS_LOW_DESC" />
                        </td>
                        <td className="col-middle">
                        </td>
                        <td className="scope-table-col  col-right">
                          <div className="scope-table-title-right">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_DOMINANCE_AXIS_HIGH" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_DOMINANCE_AXIS_HIGH_DESC" />
                        </td>
                      </tr>
                      {/* Aggressiveness */}
                      <tr>
                        <td className="scope-table-col">
                          <div className="scope-table-title-left">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_AGGRESSIVENESS_AXIS_LOW" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_AGGRESSIVENESS_AXIS_LOW_DESC" />
                        </td>
                        <td className="col-middle">
                        </td>
                        <td className="scope-table-col col-right">
                          <div className="scope-table-title-right">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_AGGRESSIVENESS_AXIS_HIGH" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_AGGRESSIVENESS_AXIS_HIGH_DESC" />
                        </td>
                      </tr>
                      {/* Conscientiousness */}
                      <tr>
                        <td className="scope-table-col">
                          <div className="scope-table-title-left">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_CONSCIENTIOUSNESS_AXIS_LOW" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_CONSCIENTIOUSNESS_AXIS_LOW_DESC" />
                        </td>
                        <td className="col-middle">
                        </td>
                        <td className="scope-table-col col-right">
                          <div className="scope-table-title-right">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_CONSCIENTIOUSNESS_AXIS_HIGH" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_CONSCIENTIOUSNESS_AXIS_HIGH_DESC" />
                        </td>
                      </tr>
                    </tbody>
                  </table>
              </div>
            }
              <BarGraph data={data} cp={cp} />
          </div>
        );
      default:
        return null;
    }
  };

  componentDidMount() {
    if(this.props.timeTaken!=="null"){ /*console.log("TIME TAKEN");*/ }else{ /*console.log("NULL TIME TAKEN");*/ }
    /*console.log("PageCharts user lang", this.props.userLanguage);*/
    
    // Handle both array and object formats for dataForGraphs
    const graphsData = Array.isArray(this.props.dataForGraphs) 
      ? this.props.dataForGraphs 
      : this.props.dataForGraphs?.dataForGraph || [];
    
    this.setState({
      graphs: graphsData,
      code: this.props.code,
      lang: this.props.userLanguage,
    });
  }

  getPoste = () => {
    try {
      const isPosteCode = (value) => /^POSTE[1-5]$/i.test(String(value || "").trim());

      const extractPosteFromNode = (node) => {
        if (!node) return "";

        if (Array.isArray(node)) {
          for (const item of node) {
            const found = extractPosteFromNode(item);
            if (found) return found;
          }
          return "";
        }

        if (typeof node !== "object") return "";

        for (const [key, value] of Object.entries(node)) {
          if (/^poste$/i.test(key) || /^POSTE_/i.test(key)) {
            if (typeof value === "string" && value.trim()) return value;
          }
          if (isPosteCode(value)) return String(value);
        }

        const nestedCandidates = [
          node.answers,
          node.results,
          node.dataForGraphs,
          node.data,
          node.user,
          node.location && node.location.state
        ];

        for (const nested of nestedCandidates) {
          const found = extractPosteFromNode(nested);
          if (found) return found;
        }

        return "";
      };

      // potential sources: location.state, props, props.user
      const locState = this.props.location && this.props.location.state ? this.props.location.state : null;
      const candidates = [locState, this.props, this.props.user, this.props.dataForGraphs];
      let val = "";
      for (const c of candidates) {
        if (!c) continue;
        if (c.POSTE) { val = c.POSTE; break; }
        if (c.poste) { val = c.poste; break; }
        // answers may be an array of single-key objects
        if (Array.isArray(c.answers)) {
          for (let i = c.answers.length - 1; i >= 0; i--) {
            const obj = c.answers[i];
            if (obj && Object.prototype.hasOwnProperty.call(obj, "POSTE")) { val = obj.POSTE; break; }
            if (obj && Object.prototype.hasOwnProperty.call(obj, "poste")) { val = obj.poste; break; }
          }
          if (val) break;
        }

        const deepFound = extractPosteFromNode(c);
        if (deepFound) {
          val = deepFound;
          break;
        }
      }
      if (!val) return "";

      // Normalize and map known POSTE codes to i18n keys
      const norm = String(val).toUpperCase().trim();
      const mapping = {
        POSTE1: "INFOPERSO5A",
        POSTE2: "INFOPERSO5B",
        POSTE3: "INFOPERSO5C",
        POSTE4: "INFOPERSO5D",
        POSTE5: "INFOPERSO5E",
      };
      if (mapping[norm]) return mapping[norm];

      // If it's not one of the known POSTE codes, return the original value
      return val;
    } catch (e) {
      return "";
    }
  };

render() {
  // DEBUG: Log graphs and FO detection
  console.log('PageCharts: this.state.graphs', this.state.graphs);
  //console.log("SO PAGE_NUMBERS", pageNumbers, this.props.locale);
  const {
    foGraph,
    faitsOpinionsGraph,
    hasFaitsOpinions,
    faitsOpinionsData,
    foDisplayData,
  } = getFaitsOpinionsMeta(this.state.graphs, { debugMatches: true });

  // DEBUG: Log foGraph
  console.log('PageCharts: foGraph', foGraph);

  // Build a BarGraph-friendly data array when backend returned FO object
  let foBarData = null;
  try {
    if (faitsOpinionsData && !Array.isArray(faitsOpinionsData)) {
      const total = Number(faitsOpinionsData.total) || 18;
      const score = Number(faitsOpinionsData.score) || 0;
      // comparisonNorms may be an array [lower, middle, upper] or an object with faitsOpinions
      let norms = [];
      if (Array.isArray(faitsOpinionsData.comparisonNorms)) norms = faitsOpinionsData.comparisonNorms;
      else if (Array.isArray(faitsOpinionsData.comparisonNorms?.faitsOpinions)) norms = faitsOpinionsData.comparisonNorms.faitsOpinions;
      // fallback sensible thresholds
      const lowerThr = Number(norms[0]) || Math.round(total * 0.33);
      const middleThr = Number(norms[1]) || Math.round(total * 0.66);
      // Build segment sizes so they sum to total
      const lowerPart = Math.max(0, lowerThr);
      const middlePart = Math.max(0, (Number(norms[1]) ? (middleThr - lowerPart) : Math.round(total * 0.33)));
      const upperPart = Math.max(0, total - lowerPart - middlePart);

      const titleText = (messages && messages.FAITSOPINIONSTITLE && messages.FAITSOPINIONSTITLE.defaultMessage) || 'Faits vs Opinions';
      // Provide translations minimally (use same string for all locales if not available)
      foBarData = [{
        fr: titleText,
        en: titleText,
        de: titleText,
        it: titleText,
        es: titleText,
        fullMark: total,
        lowerThird: lowerPart,
        middleThird: middlePart,
        upperThird: upperPart,
        user: score
      }];
    }
  } catch (e) {
    foBarData = null;
  }
  
  // Use dynamic page numbers based on locale
  const pageNumbers = getPageNumbers(this.props.locale, hasFaitsOpinions);

  const isCognitiveItem = (item) => {
    const values = [item?.fr, item?.en, item?.de, item?.it, item?.es, item?.label]
      .map((v) => String(v || '').toLowerCase().trim());

    return values.some((v) => (
      v === 'style cognitif' ||
      v === 'cognitive style' ||
      v === 'capacite de raisonnement' ||
      v === 'capacité de raisonnement' ||
      v === 'capacity for reasoning' ||
      v === 'logical reasoning' ||
      v === 'logisches denken'
    ));
  };

  const cognitiveGraph = (Array.isArray(this.state.graphs) ? this.state.graphs : []).find((g) => (
    Array.isArray(g?.data) && g.data.some((item) => isCognitiveItem(item))
  ));

  const cognitiveData = Array.isArray(cognitiveGraph?.data) ? cognitiveGraph.data : [];
  const cognitiveGraphType = cognitiveGraph?.type || 'bar';

  return (
    <div className="PageCharts">
      {this.state.graphs.map((graph, key) => {
        const p = key + 1;
        // Use the computed page numbers (adjust if FAITS/OPINIONS present)
        const keyPage = pageNumbers[key] || 15;
        const firstSectionPages = getFirstSectionPageNumbers(keyPage, hasFaitsOpinions);
        const shouldRenderMainChart = graph !== faitsOpinionsGraph;

          //console.log("page p", p, keyPage);
          return (
            <div key={key}>
              {/* Cover page */}
              {p === 1 && (
                <div className="PageCharts-chart">
                  <div className="report-alt-page">
                    <img
                      alt="ppe-logo"
                      className="report-main-logo"
                      src={ppeFullLogo}
                      style={{ position: "absolute", left: "40px", top: "40px", width: "220px" }}
                    />
                    <div className="report-alt-page-candidate">
                      <div className="candidate-separator report-title">
                        <b><SafeFormattedMessage msgKey="REPORT_TITLE" /></b>
                      </div>
                      <div className="candidate-separator">
                        <b><SafeFormattedMessage msgKey="CANDIDATE" /></b> {this.state.code}
                      </div>
                      <div className="candidate-separator">
                        <b><SafeFormattedMessage msgKey="COMPARISONGROUP" /></b>{" "}
                        {this.renderPoste()}
                      </div>
                      <div className="candidate-separator">
                        <b><SafeFormattedMessage msgKey="TEST_DATE" /></b>{" "}
                        {formatDateForDisplay(this.props.datePassed, 
                          this.props.localeDisplay || undefined,
                          { day: "2-digit", month: "long", year: "numeric" }
                        )}
                      </div>
                      <div className="candidate-separator">
                        <b><SafeFormattedMessage msgKey="TEST_TIME_TAKEN" /></b>{" "}
                        {formatDuration(this.props.timeTaken, this.props.localeDisplay || this.props.currentLanguage)}
                      </div>
                    </div>
                  </div>
                  <div className="pdf-footer"></div>
                </div>
              )}

              {/* Intro page */}
              {p === 1 && (
                <ScopeReportIntroPage
                  messages={messages}
                  email={this.state.code}
                  datePassed={this.props.datePassed}
                  pageNumber={p + 1}
                  pageNumbers={pageNumbers}
                  logo={ppeFullLogo}/>
              )}

              {/* Help modal */}
              <HelpModal
                dataTitles={graph.dataTitles}
                dataDescriptions={graph.dataDescriptions}
              />

              {/* Main chart */}
              {shouldRenderMainChart && (
              <div className="PageCharts-chart">
                <div className="header-logo-box">
                  <img
                    alt="ppe-logo"
                    className="header-logo-img"
                    src={ppeFullLogo}
                  />
                </div>
                <div className={this.props.askedfor === "admin" ? "admin-charts" : "user-charts"}>
                  <div className="PageCharts-head">
                    <div className="PageCharts-title">
                      <h1>
                        <SafeFormattedMessage msgKey={graph.title} />
                      </h1>
                      <p>
                        <SafeFormattedMessage msgKey={graph.description} />
                      </p>
                    </div>
                  </div>
                  {graph.type !== "radar" && (
                    <div className="PageCharts-svg PageCharts-svg-left">
                      {this._displayGraph(graph.type, graph.data)}
                    </div>
                  )}
                  {graph.type === "radar" && (
                    <div className="PageCharts-svg">
                      {this._displayGraph(graph.type, graph.data)}
                    </div>
                  )}
                </div>
                <div className="pdf-footer">
                  <SafeFormattedMessage msgKey="PAGE" /> {keyPage}{" "}
                  <div className="pdf-footer-separator">
                    <b><SafeFormattedMessage msgKey="CANDIDATE" /></b>
                  </div>{" "}
                  {this.state.code}
                  <div className="pdf-footer-separator">
                    <b><SafeFormattedMessage msgKey="TEST_DATE" /></b>  {formatDateForDisplay(this.props.datePassed, 
                          this.props.localeDisplay || undefined,
                          { day: "2-digit", month: "long", year: "numeric" }
                        )}
                  </div>
                </div>
              </div>
              )}

              {/* Personality traits page 1 */}
              {p === 1 && (
                <div className="PageCharts-chart">
                  <div className="header-logo-box">
                    <img
                      alt="ppe-logo"
                      className="header-logo-img"
                      src={ppeFullLogo}
                    />
                  </div>
                  <div className="report-alt-page">
                    <h1>
                      <SafeFormattedMessage msgKey={graph.title} />
                      <span className="inline font-16"> - <SafeFormattedMessage msgKey="SCOPE_REPORT_TOP_THREE" /></span>
                    </h1>
                    <ol className="list-counter">
                      {renderTraitDetailsList({
                        data: graph.data,
                        type: graph.type,
                        filterIdx: idx => idx <= 1,
                        pageNumber: p
                      })}
                    </ol>
                  </div>
                  <div className="pdf-footer">
                    <SafeFormattedMessage msgKey="PAGE" /> {firstSectionPages.traitsPage1}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="CANDIDATE" /></b>
                    </div>{" "}
                    {this.state.code}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="TEST_DATE" /></b>  {formatDateForDisplay(this.props.datePassed, 
                          this.props.localeDisplay || undefined,
                          { day: "2-digit", month: "long", year: "numeric" }
                        )}
                    </div>
                  </div>
                </div>
              )}

              {/* Personality traits page 2 */}
              {p === 1 && (
                <div className="PageCharts-chart">
                  <div className="header-logo-box">
                    <img
                      alt="ppe-logo"
                      className="header-logo-img"
                      src={ppeFullLogo}
                    />
                  </div>
                  <div className="report-alt-page">
                    <h1>
                      <SafeFormattedMessage msgKey={graph.title} />
                      <span className="inline font-16"> - <SafeFormattedMessage msgKey="SCOPE_REPORT_TOP_THREE" /></span>
                    </h1>
                    <ol className="list-counter" start="3">
                      {renderTraitDetailsList({
                        data: graph.data,
                        type: graph.type,
                        filterIdx: 2,
                        pageNumber: p
                      })}
                    </ol>
                  </div>
                  <div className="pdf-footer">
                    <SafeFormattedMessage msgKey="PAGE" /> {firstSectionPages.traitsPage2}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="CANDIDATE" /></b>
                    </div>{" "}
                    {this.state.code}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="TEST_DATE" /></b>  {formatDateForDisplay(this.props.datePassed, 
                          this.props.localeDisplay || undefined,
                          { day: "2-digit", month: "long", year: "numeric" }
                        )}
                    </div>
                  </div>
                </div>
              )}

              {/* Cognitive style */} 
              { p === 1 &&
                  <div className="PageCharts-chart">
                    <div className="header-logo-box">
                      <img
                        alt="ppe-logo"
                        className="header-logo-img"
                        src={ppeFullLogo}
                      />
                    </div>
                    <div className={this.props.askedfor === "admin" ? "admin-charts" : "user-charts"}>
                      <div className="PageCharts-head">
                        <div className="PageCharts-title">
                         
                          <h1>
                             <SafeFormattedMessage msgKey="SCOPEREPORT_CAPACITIYREASONING" />
                          </h1>
                          <p>      
                            <SafeFormattedMessage msgKey="SCOPEREPORT_CAPACITIYREASONING_DESC" />
                          </p>
                        </div>
                      </div>
                        <div className="PageCharts-svg PageCharts-svg-left">
                          {this._displayGraph(cognitiveGraphType, cognitiveData, true)}
                        </div>
                    </div>
                    <div className="pdf-footer">
                      <SafeFormattedMessage msgKey="PAGE" /> {firstSectionPages.cognitivePage}{" "}
                      <div className="pdf-footer-separator">
                        <b><SafeFormattedMessage msgKey="CANDIDATE" /></b>
                      </div>{" "}
                      {this.state.code}
                      <div className="pdf-footer-separator">
                        <b><SafeFormattedMessage msgKey="TEST_DATE" /></b>  {formatDateForDisplay(this.props.datePassed, 
                          this.props.localeDisplay || undefined,
                          { day: "2-digit", month: "long", year: "numeric" }
                        )}
                      </div>
                    </div>
                  </div>
              }

              {/* Faits vs Opinions */} 
              { p === 1 && hasFaitsOpinions &&
                  <div className="PageCharts-chart">
                    <div className="header-logo-box">
                      <img
                        alt="ppe-logo"
                        className="header-logo-img"
                        src={ppeFullLogo}
                      />
                    </div>
                    <div className={this.props.askedfor === "admin" ? "admin-charts" : "user-charts"}>
                      <div className="PageCharts-head">
                        <div className="PageCharts-title">  
                          <h1>
                             <SafeFormattedMessage msgKey="FAITSOPINIONSTITLE" />
                          </h1>
                          <p>      
                            <SafeFormattedMessage msgKey="SCOPEREPORT_FAITS_VS_OPINIONS_DESC" />
                          </p>
                        </div>
                      </div>
                        <div className="PageCharts-svg PageCharts-svg-left">
                          {console.log('FAITS_OPINIONS data:', faitsOpinionsData)}
                          {console.log('foBarData:', foBarData)}
                          {/* Render bar graph when available */}
                          {foBarData && (
                            <div className="faits-opinions-graph">
                              <BarGraph data={foBarData} cp={false} />
                            </div>
                          )}

                          {/* Render simple summary header (no question list) */}
                          {faitsOpinionsData && (
                            <div className="faits-opinions-summary">
                              <h3>
                                <SafeFormattedMessage msgKey="FAITSOPINIONSTITLE" /> - <b>{faitsOpinionsData.score}/{faitsOpinionsData.total}</b>
                              </h3>
                            </div>
                          )}
                          {/* Fallback: if no breakdown and no foBarData, try to render using foDisplayData */}
                          {!faitsOpinionsData && !foBarData && this._displayGraph(graph.type, foDisplayData, false)}
                        </div>
                        <table className="scope-table-fullwidth FO-pro-desc">
                          <tbody>
                            <tr>
                              <td className="scope-table-col"><h3><SafeFormattedMessage msgKey="SCOPEREPORT_FAITS_VS_OPINIONS_SUBTITLE" /></h3></td>
                            </tr>
                            <tr>
                              <td className="scope-table-col"><SafeFormattedMessage msgKey="SCOPEREPORT_FAITS_VS_OPINIONS_EXPLANATION" /></td>
                            </tr>
                          </tbody>
                        </table>
                    </div>
                    <div className="pdf-footer">
                      <SafeFormattedMessage msgKey="PAGE" /> {firstSectionPages.faitsOpinionsPage}{" "}
                      <div className="pdf-footer-separator">
                        <b><SafeFormattedMessage msgKey="CANDIDATE" /></b>
                      </div>{" "}
                      {this.state.code}
                      <div className="pdf-footer-separator">
                        <b><SafeFormattedMessage msgKey="TEST_DATE" /></b>  {formatDateForDisplay(this.props.datePassed, 
                          this.props.localeDisplay || undefined,
                          { day: "2-digit", month: "long", year: "numeric" }
                        )}
                      </div>
                    </div>
                  </div>
              }
              
              {/* Coping Definitions page 1 */}
              {p === 2 && (
                 <>
                    <CopingDefinitionsPages
                      graph={graph}
                      code={this.state.code}
                      datePassed={this.props.datePassed}
                      keyPage={keyPage}
                      getThirdPosition={getThirdPosition}
                      SafeFormattedMessage={SafeFormattedMessage}
                    />
                      <CopingDefinitionsPages2
                      graph={graph}
                      code={this.state.code}
                      datePassed={this.props.datePassed}
                      keyPage={keyPage}
                      getThirdPosition={getThirdPosition}
                      SafeFormattedMessage={SafeFormattedMessage}
                    />
                  </>

              )}

              {/* Emotionality Definitions page 1 */}
              {p === 3 && (
                <div className="PageCharts-chart">
                  <div className="header-logo-box">
                    <img
                      alt="ppe-logo"
                      className="header-logo-img"
                      src={ppeFullLogo}
                    />
                  </div>
                  <div className="report-alt-page">
                    <h1>
                      <SafeFormattedMessage msgKey={graph.title} />
                      <span className="inline font-16"> - <SafeFormattedMessage msgKey="DEFINITIONS" /></span>
                    </h1>
                    <table className="scope-table-fullwidth">
                      <tbody>
                        {/* Serenity */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_SERENITY" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_SERENITY_DESC" />
                          </td>
                        </tr>
                        {/* Anxiety */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_ANXIETY" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_ANXIETY_DESC" />
                          </td>
                        </tr>
                        {/* Negative Affect */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_NEGATIVEAFFECT" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_NEGATIVEAFFECT_DESC" />
                          </td>
                        </tr>
                        {/* Self-Consciousness */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_SELFCONSCIOUSNESS" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_SELFCONSCIOUSNESS_DESC" />
                          </td>
                        </tr>
                        {/* Irritability */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_IRRITABILITY" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_IRRITABILITY_DESC" />
                          </td>
                        </tr>
                      </tbody>
                    </table>          
                  </div>
                  <div className="pdf-footer">
                    <SafeFormattedMessage msgKey="PAGE" /> {keyPage + 1}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="CANDIDATE" /></b>
                    </div>{" "}
                    {this.state.code}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="TEST_DATE" /></b>  {formatDateForDisplay(this.props.datePassed, 
                          this.props.localeDisplay || undefined,
                          { day: "2-digit", month: "long", year: "numeric" }
                        )}
                    </div>
                  </div>
                </div>
              )}

              {/* Workplace Definitions page */}
              {p === 5 && (
                <div className="PageCharts-chart">
                  <div className="header-logo-box">
                    <img
                      alt="ppe-logo"
                      className="header-logo-img"
                      src={ppeFullLogo}
                    />
                  </div>
                  <div className="report-alt-page">
                    <h1>
                      <SafeFormattedMessage msgKey="SCOPE_REPORT_VALUEPREF_TITLE" />
                      <span className="inline font-16"> - <SafeFormattedMessage msgKey="DEFINITIONS" /></span>
                    </h1>
                    <table className="scope-table-fullwidth">
                      <tbody>
                        {/* Plaisir au travail */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_ENJOY_WORK" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_ENJOY_WORK_DESC" />
                          </td>
                        </tr>
                        {/* Leadership */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_LEADERSHIP" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_LEADERSHIP_DESC" />
                          </td>
                        </tr>
                        {/* SÃ©curitÃ© de l'emploi */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_JOB_SECURITY" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_JOB_SECURITY_DESC" />
                          </td>
                        </tr>
                        {/* Stimulation intellectuelle */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_INTELLECTUAL_STIMULATION" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_INTELLECTUAL_STIMULATION_DESC" />
                          </td>
                        </tr>
                        {/* Salaire */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_SALARY" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_SALARY_DESC" />
                          </td>
                        </tr>
                        {/* TÃ¢ches variÃ©es */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_TASK_VARIABILITY" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_TASK_VARIABILITY_DESC" />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="pdf-footer">
                    <SafeFormattedMessage msgKey="PAGE" /> {keyPage + 1}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="CANDIDATE" /></b>
                    </div>{" "}
                    {this.state.code}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="TEST_DATE" /></b>  {formatDateForDisplay(this.props.datePassed, 
                          this.props.localeDisplay || undefined,
                          { day: "2-digit", month: "long", year: "numeric" }
                        )}
                    </div>
                  </div>
                </div>
              )}

              {/* Workplace Values top 3 traits page 1 */}
              {p === 5 && (
                <div className="PageCharts-chart">
                  <div className="header-logo-box">
                    <img
                      alt="ppe-logo"
                      className="header-logo-img"
                      src={ppeFullLogo}
                    />
                  </div>
                  <div className="report-alt-page">
                    <h1>
                      <SafeFormattedMessage msgKey={graph.title} />
                      <span className="inline font-16"> - <SafeFormattedMessage msgKey="SCOPE_REPORT_TOP_THREE" /></span>
                    </h1>
                    <ol className="list-counter">
                      {renderTraitDetailsList({
                        data: graph.data,
                        type: graph.type,
                        filterIdx: idx => idx <= 1,
                        pageNumber: p
                      })}
                    </ol>
                  </div>
                  <div className="pdf-footer">
                    <SafeFormattedMessage msgKey="PAGE" /> {keyPage + 2}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="CANDIDATE" /></b>
                    </div>{" "}
                    {this.state.code}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="TEST_DATE" /></b>  {formatDateForDisplay(this.props.datePassed, 
                          this.props.localeDisplay || undefined,
                          { day: "2-digit", month: "long", year: "numeric" }
                        )}
                    </div>
                  </div>
                </div>
              )}

              {/* Workplace Values top 3 traits page 2 */}
              {p === 5 && (
                <div className="PageCharts-chart">
                  <div className="header-logo-box">
                    <img
                      alt="ppe-logo"
                      className="header-logo-img"
                      src={ppeFullLogo}
                    />
                  </div>
                  <div className="report-alt-page">
                    <h1>
                      <SafeFormattedMessage msgKey={graph.title} />
                      <span className="inline font-16"> - <SafeFormattedMessage msgKey="SCOPE_REPORT_TOP_THREE" /></span>
                    </h1>
                    <ol className="list-counter" start="3">
                      {renderTraitDetailsList({
                        data: graph.data,
                        type: graph.type,
                        filterIdx: 2,
                        pageNumber: p
                      })}
                    </ol>
                  </div>
                  <div className="pdf-footer">
                    <SafeFormattedMessage msgKey="PAGE" /> {keyPage + 3}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="CANDIDATE" /></b>
                    </div>{" "}
                    {this.state.code}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="TEST_DATE" /></b>  {formatDateForDisplay(this.props.datePassed, 
                          this.props.localeDisplay || undefined,
                          { day: "2-digit", month: "long", year: "numeric" }
                        )}
                    </div>
                  </div>
                </div>
              )}

              {/* Value hierarchy Definitions page */}
              {p === 6 && (
                <div className="PageCharts-chart">
                <div className="header-logo-box">
                    <img
                      alt="ppe-logo"
                      className="header-logo-img"
                      src={ppeFullLogo}
                    />
                </div>
                  <div className="report-alt-page">
                    <h1>
                      <SafeFormattedMessage msgKey="SCOPEREPORT_VALUEPREFERENCES2" />
                      <span className="inline font-16"> - <SafeFormattedMessage msgKey="DEFINITIONS" /></span>
                    </h1>
                    <table className="scope-table-fullwidth">
                      <tbody>
                        {/* Plaisir au travail */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_POWER" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_POWER_DESC" />
                          </td>
                        </tr>
                        {/* Leadership */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_SUCCESS" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_SUCCESS_DESC" />
                          </td>
                        </tr>
                        {/* SÃ©curitÃ© de l'emploi */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_MORALITY" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_MORALITY_DESC" />
                          </td>
                        </tr>
                        {/* Stimulation intellectuelle */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_HONOR" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_HONOR_DESC" />
                          </td>
                        </tr>
                        {/* Salaire */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_AFFECTION" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_AFFECTION_DESC" />
                          </td>
                        </tr>
                        {/* TÃ¢ches variÃ©es */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_HARMONY" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_HARMONY_DESC" />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="pdf-footer">
                    <SafeFormattedMessage msgKey="PAGE" /> {keyPage + 1}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="CANDIDATE" /></b>
                    </div>{" "}
                    {this.state.code}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="TEST_DATE" /></b>  {formatDateForDisplay(this.props.datePassed, 
                          this.props.localeDisplay || undefined,
                          { day: "2-digit", month: "long", year: "numeric" }
                        )}
                    </div>
                  </div>
                </div>
              )}

              {/* Value hierarchy top 3 traits page 1 */}
              {p === 6 && (
                <div className="PageCharts-chart">
                <div className="header-logo-box">
                    <img
                      alt="ppe-logo"
                      className="header-logo-img"
                      src={ppeFullLogo}
                    />
                </div>
                  <div className="report-alt-page">
                    <h1>
                      <SafeFormattedMessage msgKey={graph.title} />
                      <span className="inline font-16"> - <SafeFormattedMessage msgKey="SCOPE_REPORT_TOP_THREE" /></span>
                    </h1>
                    <ol className="list-counter">
                      {renderTraitDetailsList({
                        data: graph.data,
                        type: graph.type,
                        filterIdx: idx => idx <= 1,
                        pageNumber: p
                      })}
                    </ol>
                  </div>
                  <div className="pdf-footer">
                    <SafeFormattedMessage msgKey="PAGE" /> {keyPage + 2}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="CANDIDATE" /></b>
                    </div>{" "}
                    {this.state.code}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="TEST_DATE" /></b>  {formatDateForDisplay(this.props.datePassed, 
                          this.props.localeDisplay || undefined,
                          { day: "2-digit", month: "long", year: "numeric" }
                        )}
                    </div>
                  </div>
                </div>
              )}

              {/* Value hierarchy top 3 traits page 2 */}
              {p === 6 && (
                <div className="PageCharts-chart">
                  <div className="header-logo-box">
                    <img
                      alt="ppe-logo"
                      className="header-logo-img"
                      src={ppeFullLogo}
                    />
                  </div>
                  <div className="report-alt-page">
                    <h1>
                      <SafeFormattedMessage msgKey={graph.title} />
                      <span className="inline font-16"> - <SafeFormattedMessage msgKey="SCOPE_REPORT_TOP_THREE" /></span>
                    </h1>
                    <ol className="list-counter" start="3">
                      {renderTraitDetailsList({
                        data: graph.data,
                        type: graph.type,
                        filterIdx: 2,
                        pageNumber: p
                      })}
                    </ol>
                  </div>
                  <div className="pdf-footer">
                    <SafeFormattedMessage msgKey="PAGE" /> {keyPage + 3}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="CANDIDATE" /></b>
                    </div>{" "}
                    {this.state.code}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="TEST_DATE" /></b>  {formatDateForDisplay(this.props.datePassed, 
                          this.props.localeDisplay || undefined,
                          { day: "2-digit", month: "long", year: "numeric" }
                        )}
                    </div>
                  </div>
                </div>
              )}

              {/* Personal Notes Page - LAST PAGE */}
              {p === 6 && (
                <div className="PageCharts-chart">
                  <div className="header-logo-box">
                      <img
                        alt="ppe-logo"
                        className="header-logo-img"
                        src={ppeFullLogo}
                      />
                  </div>
                  <div className="header-logo-box">
                    <img
                      alt="ppe-logo"
                      className="header-logo-img"
                      src={ppeFullLogo}
                    />
                  </div>
                  <div className="report-alt-page">
                    <h1>
                      <SafeFormattedMessage msgKey="SCOPE_REPORT_PERSONAL_NOTES_TITLE" />
                    </h1>
                    <div className="personal-notes-section">
                      <div className="personal-notes-block">
                        <b><SafeFormattedMessage msgKey="SCOPE_REPORT_PERSONAL_NOTES_STRENGTHS" /></b>
                        <div className="personal-notes-italic">
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_PERSONAL_NOTES_STRENGTHS_EXAMPLE" />
                        </div>
                        <div className="personal-notes-line"></div>
                        <div className="personal-notes-line"></div>
                        <div className="personal-notes-line personal-notes-line-space"></div>
                      </div>
                      <div className="personal-notes-block">
                        <b><SafeFormattedMessage msgKey="SCOPE_REPORT_PERSONAL_NOTES_STRENGTHS_CONTEXTS" /></b>
                        <div className="personal-notes-italic">
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_PERSONAL_NOTES_STRENGTHS_CONTEXTS_EXAMPLE" />
                        </div>
                        <div className="personal-notes-line"></div>
                        <div className="personal-notes-line"></div>
                        <div className="personal-notes-line personal-notes-line-space"></div>
                      </div>
                      <div className="personal-notes-block">
                        <b><SafeFormattedMessage msgKey="SCOPE_REPORT_PERSONAL_NOTES_DEVELOPMENT" /></b>
                        <div className="personal-notes-italic">
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_PERSONAL_NOTES_DEVELOPMENT_EXAMPLE" />
                        </div>
                        <div className="personal-notes-line"></div>
                        <div className="personal-notes-line"></div>
                        <div className="personal-notes-line"></div>
                        <div className="personal-notes-line personal-notes-line-space"></div>
                      </div>
                      <div className="personal-notes-block">
                        <b><SafeFormattedMessage msgKey="SCOPE_REPORT_PERSONAL_NOTES_OBSTACLES" /></b>
                        <div className="personal-notes-italic">
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_PERSONAL_NOTES_OBSTACLES_EXAMPLE" />
                        </div>
                        <div className="personal-notes-line"></div>
                        <div className="personal-notes-line"></div>
                        <div className="personal-notes-line"></div>
                        <div className="personal-notes-line personal-notes-line-space"></div>
                      </div>
                      <div className="personal-notes-block">
                        <b><SafeFormattedMessage msgKey="SCOPE_REPORT_PERSONAL_NOTES_MOTIVATION" /></b>
                        <div className="personal-notes-italic">
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_PERSONAL_NOTES_MOTIVATION_EXAMPLE" />
                        </div>
                        <div className="personal-notes-line"></div>
                        <div className="personal-notes-line"></div>
                        <div className="personal-notes-line"></div>
                        <div className="personal-notes-line"></div>
                      </div>
                    </div>
                  </div>
                  <div className="pdf-footer">
                    <SafeFormattedMessage msgKey="PAGE" /> {keyPage + 4}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="CANDIDATE" /></b>
                    </div>{" "}
                    {this.state.code}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="TEST_DATE" /></b>  {formatDateForDisplay(this.props.datePassed, 
                          this.props.localeDisplay || undefined,
                          { day: "2-digit", month: "long", year: "numeric" }
                        )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }
}

export default PageChartsWithContext;
